import { CircleMarker, Polyline, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Map } from "@/components/ui/map";
import { RelocationFlowCity, RelocationFlowMovement } from "@/gql/schema";
import { useTranslations } from "use-intl";
import { BasemapLayer } from "@/components/map/BasemapLayer";

interface Props {
  cities: RelocationFlowCity[];
  movements: RelocationFlowMovement[];
  showDepartures: boolean;
  showDeliveries: boolean;
  showRoutes: boolean;
}

const DEPARTURE_COLOR = "#2563eb";
const DELIVERY_COLOR = "#059669";
const ROUTE_COLOR = "#6366f1";

function getRadius(count: number, maxCount: number) {
  const minRadius = 8;
  const maxRadius = 40;
  return minRadius + (count / maxCount) * (maxRadius - minRadius);
}

export function RelocationFlowMap({
  cities,
  movements,
  showDepartures,
  showDeliveries,
  showRoutes,
}: Props) {
  const t = useTranslations("report");

  const cityCoords: Record<string, RelocationFlowCity> = {};
  cities.forEach((c) => {
    cityCoords[c.city_id] = c;
  });

  const corridors = movements
    .filter((m) => m.departure_city_id !== m.delivery_city_id)
    .map((m) => {
      const from = cityCoords[m.departure_city_id];
      const to = cityCoords[m.delivery_city_id];
      return from && to ? { movement: m, from, to } : null;
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);

  const maxMovementCount = Math.max(
    ...corridors.map((c) => c.movement.count),
    1,
  );

  const maxDeparture = Math.max(...cities.map((c) => c.departure_count), 1);
  const maxDelivery = Math.max(...cities.map((c) => c.delivery_count), 1);

  function getLineWeight(count: number) {
    const minWeight = 1;
    const maxWeight = 8;
    return minWeight + (count / maxMovementCount) * (maxWeight - minWeight);
  }

  const center: [number, number] =
    cities.length > 0
      ? [
          cities.reduce((sum, c) => sum + c.lat, 0) / cities.length,
          cities.reduce((sum, c) => sum + c.lng, 0) / cities.length,
        ]
      : [-25.2744, 133.7751];

  function renderBubble(
    city: RelocationFlowCity,
    variant: "departures" | "deliveries",
  ) {
    const count =
      variant === "departures" ? city.departure_count : city.delivery_count;

    if (count === 0) {
      return null;
    }

    const median =
      variant === "departures"
        ? city.median_days_to_book_departure
        : city.median_days_to_book_delivery;
    const color = variant === "departures" ? DEPARTURE_COLOR : DELIVERY_COLOR;
    const maxCount = variant === "departures" ? maxDeparture : maxDelivery;

    return (
      <CircleMarker
        key={`${variant}-${city.city_id}`}
        center={[city.lat, city.lng]}
        radius={getRadius(count, maxCount)}
        pathOptions={{
          fillColor: color,
          color: color,
          weight: 2,
          opacity: 0.8,
          fillOpacity: 0.4,
        }}
      >
        <Popup>
          <div className="text-center">
            <div className="font-semibold">{city.city_name}</div>
            <div className="text-sm">
              {variant === "departures" ? t("departures") : t("deliveries")}:{" "}
              <span className="font-bold">{count}</span>
            </div>
            {median !== null && median !== undefined ? (
              <div className="text-sm">
                {t("medianDaysToBook")}:{" "}
                <span className="font-bold">
                  {t("dayCount", { count: median })}
                </span>
              </div>
            ) : null}
          </div>
        </Popup>
      </CircleMarker>
    );
  }

  if (cities.length === 0) {
    return (
      <div className="flex h-[500px] items-center justify-center rounded-lg border bg-gray-50 text-gray-500">
        {t("noDataForPeriod")}
      </div>
    );
  }

  return (
    <div className="h-[500px] w-full overflow-hidden rounded-lg border">
      <Map
        center={center}
        zoom={4}
        scrollWheelZoom={true}
        className="h-full w-full"
      >
        <BasemapLayer />
        {showRoutes
          ? corridors.map(({ movement, from, to }) => (
              <Polyline
                key={`${movement.departure_city_id}-${movement.delivery_city_id}`}
                positions={[
                  [from.lat, from.lng],
                  [to.lat, to.lng],
                ]}
                pathOptions={{
                  color: ROUTE_COLOR,
                  weight: getLineWeight(movement.count),
                  opacity: 0.3 + 0.5 * (movement.count / maxMovementCount),
                }}
              >
                <Popup>
                  <div className="text-center">
                    <div className="font-semibold">
                      {movement.departure_city_name} →{" "}
                      {movement.delivery_city_name}
                    </div>
                    <div className="text-sm">
                      {t("corridorBookings")}:{" "}
                      <span className="font-bold">{movement.count}</span>
                    </div>
                  </div>
                </Popup>
              </Polyline>
            ))
          : null}
        {showDepartures
          ? cities.map((city) => renderBubble(city, "departures"))
          : null}
        {showDeliveries
          ? cities.map((city) => renderBubble(city, "deliveries"))
          : null}
      </Map>
    </div>
  );
}
